// dependencies
import React, { useState } from "react";
import { useIntl } from "react-intl";

// containers
import PlainAside from "./Plain";
import ResponsiveAside from "./Responsive";

// helpers
import {
  bookingEstimateGetList,
  bookingEstimateHasList,
  bookingEstimateGetPrice,
  bookingEstimateHasPrice,
  bookingEstimateHasResume,
  bookingEstimateGetResume,
  bookingEstimateHasAdvance,
  bookingEstimateGetAdvance,
  bookingEstimateGetErrorMessage,
  bookingEstimateGetTotal,
} from "@cloudspire/legacy-resources/src/helpers/booking/estimate";
import {
  pricingEstimateHasPrice,
  pricingEstimateGetPrice,
} from "@cloudspire/legacy-resources/src/helpers/pricing/estimate";
import { roomIsBookableOnline } from "@cloudspire/legacy-resources/src/helpers/room";

// hooks
import useViewportMedia from "@cloudspire/legacy-resources/src/hocs/useViewportMedia";
import { useResponse } from "@cloudspire/legacy-resources/src/hooks/fetcher";

// contexts
import { useRequestContext } from "@cloudspire/legacy-resources/src/contexts/requestContext";
import { useConfiguration } from "@cloudspire/legacy-resources/src/contexts/configuration";

// api
import { roomMetricCreate } from "@cloudspire/legacy-resources/src/api/room";
import { pricingEstimateAside } from "@cloudspire/legacy-resources/src/api/pricing";

// helpers
import { errorMonolithFormatErrors } from "@cloudspire/legacy-resources/src/helpers/error";
import bookingDelegationGetChannelId from "@cloudspire/legacy-resources/src/helpers/booking/delegation/getChannelId";

function BookingAsideContainer(props) {
  const {
    room,
    type,
    parameterList,
    shouldRenderLegend,
    bookingEstimateResponse,
    bookingReference,
    shouldRenderBookingDetailButton,
    onChange,
  } = props;

  const intl = useIntl();

  const requestContext = useRequestContext();

  const { matches } = useViewportMedia({
    queries: [
      {
        minWidth: 1100,
      },
    ],
  });

  const configuration = useConfiguration();

  const roomReference = parameterList?.roomReference ?? room?.reference;

  const pricingEstimateResponse = useResponse(
    pricingEstimateAside({
      roomReference,
      channelId: bookingDelegationGetChannelId({ configuration, room }),
    })
  );

  const pricing = pricingEstimateResponse.data?.data;

  const roomPricing = pricing?.[roomReference];

  const hasBookingEstimateList = bookingEstimateHasList({
    bookingEstimate: bookingEstimateResponse?.data?.data,
  });
  const hasBookingEstimateResume = bookingEstimateHasResume({
    bookingEstimate: bookingEstimateResponse?.data?.data,
  });
  const hasBookingEstimatePrice = bookingEstimateHasPrice({
    bookingEstimate: bookingEstimateResponse?.data?.data,
  });
  const hasBookingEstimateAdvance = bookingEstimateHasAdvance({
    bookingEstimate: bookingEstimateResponse?.data?.data,
  });
  const hasPricingEstimatePrice = pricingEstimateHasPrice({
    pricingEstimate: roomPricing,
  });

  const bookingEstimatePrice = bookingEstimateGetPrice({
    bookingEstimate: bookingEstimateResponse?.data?.data,
    isBookable: roomIsBookableOnline({ room, configuration }),
  });

  const pricingEstimatePrice = pricingEstimateGetPrice({
    pricingEstimate: roomPricing,
  });

  /**
   * Gère le clic sur le bouton "Nous contacter".
   */
  function handlePressContactUs() {
    // TODO: GTM
  }

  /**
   * Gère le clic sur le bouton de contact propriétaire par e-mail.
   */
  function handlePressOwnerContactMail() {
    // TODO: GTM
  }

  const [metricCalled, setMetricCalled] = useState(false);

  /**
   * Gère le clic sur le bouton de contact propriétaire par téléphone.
   */
  function handlePressOwnerContactPhone() {
    // TODO: GTM

    if (!metricCalled) {
      setMetricCalled(true);

      roomMetricCreate({
        requestContext,
        parameters: {
          roomReference: room.reference,
        },
        body: {
          metric: "contact_infos",
        },
      });
    }
  }

  /**
   * Gère le clic sur le numéro de téléphone.
   */
  function handlePressContactPhone() {
    // TODO: GTM
  }

  /**
   * Gère le clic sur le contact par e-mail
   */
  function handlePressContactMail() {
    // TODO: GTM
  }

  /**
   * Quelle source de données est à prioriser pour afficher le prix (estimate ou pricing).
   */
  const priceToRender =
    "room" === type
      ? "" !== (parameterList?.checkin ?? "") &&
        "" !== (parameterList?.checkout ?? "") &&
        "" !== (parameterList?.adults ?? "")
        ? "booking"
        : "pricing"
      : "booking";

  const Aside = matches ? PlainAside : ResponsiveAside;

  const bookingEstimateErrorList = errorMonolithFormatErrors({
    errorList: bookingEstimateResponse?.error?.body?.errors ?? {},
    intl,
  });

  const pricingEstimateErrorList = errorMonolithFormatErrors({
    errorList: pricingEstimateResponse?.error?.body?.errors ?? {},
    intl,
  });

  const errorList = {
    ...bookingEstimateErrorList,
    ...pricingEstimateErrorList,
  };

  return (
    <>
      <Aside
        priceToRender={priceToRender}
        errorMessage={bookingEstimateGetErrorMessage({ errorList, intl })}
        hasBookingEstimateList={hasBookingEstimateList}
        hasBookingEstimateResume={hasBookingEstimateResume}
        hasBookingEstimatePrice={hasBookingEstimatePrice}
        hasBookingEstimateAdvance={hasBookingEstimateAdvance}
        hasPricingEstimatePrice={hasPricingEstimatePrice}
        type={type}
        list={bookingEstimateGetList({
          bookingEstimate: bookingEstimateResponse?.data?.data,
          isBookable: roomIsBookableOnline({ room, configuration }),
          intl,
        })}
        total={bookingEstimateGetTotal({
          bookingEstimate: bookingEstimateResponse?.data?.data,
          intl,
        })}
        resume={bookingEstimateGetResume({
          bookingEstimate: bookingEstimateResponse?.data?.data,
        })}
        bookingEstimatePrice={bookingEstimatePrice}
        pricingEstimatePrice={pricingEstimatePrice}
        advance={bookingEstimateGetAdvance({
          bookingEstimate: bookingEstimateResponse?.data?.data,
        })}
        room={room}
        pricingEstimateResponse={pricingEstimateResponse}
        bookingEstimateResponse={bookingEstimateResponse}
        parameterList={parameterList}
        shouldRenderLegend={shouldRenderLegend}
        bookingReference={bookingReference}
        shouldRenderBookingDetailButton={shouldRenderBookingDetailButton}
        onChange={onChange}
        onPressContactUs={handlePressContactUs}
        onPressOwnerContactMail={handlePressOwnerContactMail}
        onPressOwnerContactPhone={handlePressOwnerContactPhone}
        onPressContactPhone={handlePressContactPhone}
        onPressContactMail={handlePressContactMail}
      />
    </>
  );
}

export default BookingAsideContainer;
